export type LegalDocument = {
  readonly title: string;
  readonly description: string;
  readonly intro: ReadonlyArray<string>;
  readonly sections: ReadonlyArray<{
    readonly title: string;
    readonly body: ReadonlyArray<string>;
    readonly items?: ReadonlyArray<string>;
  }>;
};

const company =
  "MaitoAI, LLC, a Delaware limited liability company with an address at 1111b South Governors Ave STE 25502, Dover, DE 19904, United States";
export const LEGAL_CONTACT_EMAIL = "support@maito.app";
export const LEGAL_LAST_UPDATED = "October 9, 2026";

export const termsDocument = {
  title: "Terms of Service",
  description:
    "Terms governing use of Maito's web, desktop, and mobile clients, agent workflows, managed environments, and integrations.",
  intro: [
    'These Terms of Service ("Terms") govern your use of Maito, including our website, applications, hosted services, account connections, documentation, and related services (the "Service").',
    "Maito helps individuals and teams work with AI agents through shared workspaces, conversations, coding workflows, scheduled tasks, and connected tools. You can run your own environment or use a managed environment.",
  ],
  sections: [
    {
      title: "Agreement",
      body: [
        `Maito is operated by ${company}.`,
        "By creating an account or using the Service, you agree to these Terms. If you use Maito for a company, client, or other organization, you represent that you have authority to bind that organization.",
      ],
    },
    {
      title: "The Service",
      body: [
        "The Service may include web, desktop, and mobile clients, local and remote environments, managed hosting, AI provider connections, conversations, repository access, coding tasks, checkpoints, knowledge, memory, skills, scheduled workflows, tool integrations, and collaboration through services such as Slack and MCP.",
        "Supported features may vary by client, provider, plan, permissions, and availability. Maito does not give you ownership of or additional rights to third-party models, accounts, credentials, software, or data sources.",
      ],
    },
    {
      title: "Accounts and credentials",
      body: [
        "You are responsible for your account, workspace members, environments, connected accounts, API keys, secrets, and activity under your credentials. Keep credentials secure, grant access only to authorized people, and contact us promptly if you believe your account or environment has been compromised.",
        "You may not share credentials publicly, use another customer's credentials, bypass permissions or usage limits, or create accounts to evade enforcement.",
      ],
    },
    {
      title: "Customer content and data",
      body: [
        "You retain your rights to the prompts, conversations, code, files, repository data, documents, and other materials you submit to Maito, subject to rights held by third parties.",
        "You grant us permission to host, store, retrieve, transmit, display, transform, and otherwise process your content as needed to provide the Service, follow your instructions, secure the Service, enforce these Terms, and comply with law. This includes sending relevant content to AI providers and connected tools when your workflows require it.",
        "You are responsible for having the rights, permissions, and legal basis needed for the content, account access, and instructions you provide.",
      ],
    },
    {
      title: "AI agents and your responsibilities",
      body: [
        "AI output can be inaccurate, incomplete, or unsuitable for your intended use. You are responsible for reviewing and validating output, code changes, commands, and other agent actions before relying on them, especially in sensitive or consequential contexts.",
        "Agents may read or modify files, run commands, access connected services, and take actions using the permissions you grant. Configure access, approvals, integrations, and environment permissions appropriately. Maintain backups and review changes before deploying or sharing them. Checkpoints and other recovery features are not a substitute for your own backups.",
        "We do not guarantee the accuracy, originality, legality, security, or fitness of AI output, or that output will be unique. Rights to provider output may also be governed by the relevant provider's terms.",
      ],
    },
    {
      title: "Connected providers and platforms",
      body: [
        "Maito works with third-party AI providers, source-control services, messaging platforms, MCP servers, and other tools. You are responsible for complying with their terms, privacy policies, subscription requirements, permission scopes, and usage limits. AI provider subscriptions and API charges are separate from Maito charges unless your plan expressly states otherwise.",
        "Third parties may change, reject, delay, restrict, or stop actions requested through Maito. We are not responsible for their outages, policy changes, account restrictions, model behavior, API changes, or data handling practices.",
      ],
    },
    {
      title: "Self-hosted and remote environments",
      body: [
        "When you run Maito on your own infrastructure, you are responsible for that environment's security, network access, updates, storage, backups, credentials, and operation. Connecting remotely or sharing an environment allows authorized clients and members to access the data and capabilities available to them.",
        "Managed environments are subject to the limits and lifecycle of your plan. Export data you need before deleting an environment or ending paid access; environment files and backups may be permanently removed when access ends.",
      ],
    },
    {
      title: "Acceptable use",
      body: ["You may not use the Service to:"],
      items: [
        "Violate law, sanctions, export controls, privacy rights, intellectual property rights, third-party policies, or contractual obligations.",
        "Create or distribute spam, phishing, malware, deceptive, harassing, exploitative, illegal, or harmful content.",
        "Impersonate people or organizations without authorization, or access systems, accounts, repositories, or data without permission.",
        "Collect, process, or disclose sensitive personal information without the required consent and legal basis.",
        "Interfere with the Service or others through excessive load, malicious traffic, credential attacks, or vulnerability exploitation.",
        "Resell or provide managed access to our hosted Service except as permitted by your plan or a written agreement. This does not restrict rights granted by applicable open-source licenses.",
      ],
    },
    {
      title: "Plans, billing, and taxes",
      body: [
        "Pricing, subscriptions, included credits, usage limits, and any additional charges are shown in the Service, on our pricing page, or in an applicable order form. By purchasing a plan or credits, or enabling automatic top-ups, you authorize the charges disclosed at purchase. Recurring subscriptions renew until cancelled under the applicable plan terms.",
        "Payments are processed by Stripe or another payment provider. Fees are non-refundable except where required by law or expressly agreed in writing. You are responsible for applicable taxes, duties, and similar charges.",
        "We may change pricing, limits, included features, and billing models prospectively. If you do not agree to a change, you can cancel or stop using the paid Service before the change applies.",
      ],
    },
    {
      title: "Availability and changes",
      body: [
        "The Service is provided without uptime guarantees unless a separate written agreement says otherwise. Maintenance, incidents, provider outages, rate limits, queue delays, and security issues may affect availability or behavior.",
        "We may add, change, suspend, or discontinue features, integrations, clients, APIs, or hosted services. We may provide migration guidance for material changes when commercially reasonable.",
      ],
    },
    {
      title: "Intellectual property and open source",
      body: [
        "Maito's trademarks, logos, visual design, and proprietary materials are owned by us or our licensors. We grant you a limited, non-exclusive, non-transferable right to use the Service in accordance with these Terms.",
        "Maito includes open-source software. That software is governed by its applicable license, which controls your rights to use, copy, modify, and distribute it. These Terms do not override or limit rights granted by an open-source license. Other third-party materials remain subject to their own licenses.",
      ],
    },
    {
      title: "Feedback",
      body: [
        "If you send feedback, suggestions, or feature requests, you grant us the right to use them without restriction or compensation.",
      ],
    },
    {
      title: "Copyright and rights complaints",
      body: [
        `If you believe content or activity on Maito infringes your rights, contact ${LEGAL_CONTACT_EMAIL} with the work or right at issue, the location of the material, your contact information, and any statements required by applicable law.`,
      ],
    },
    {
      title: "Suspension and termination",
      body: [
        "You may stop using the Service at any time. We may suspend or terminate access, restrict credentials, pause workflows, or remove content if you violate these Terms, fail to pay, create risk for others, violate provider requirements, or if required by law.",
        "After termination, your right to use our hosted Service ends. We may retain information as described in our Privacy Policy for legal, billing, security, backup, fraud-prevention, or operational reasons. Termination does not revoke rights granted under applicable open-source licenses.",
      ],
    },
    {
      title: "Disclaimers",
      body: [
        'The Service is provided "as is" and "as available." To the fullest extent permitted by law, we disclaim all warranties, including implied warranties of merchantability, fitness for a particular purpose, title, non-infringement, and uninterrupted or error-free operation.',
        "We do not guarantee that agent tasks, code changes, commands, integrations, schedules, messages, checkpoints, or other requested actions will succeed, arrive on time, remain recoverable, or produce the result you expect.",
      ],
    },
    {
      title: "Limitation of liability",
      body: [
        "To the fullest extent permitted by law, MaitoAI, LLC and its affiliates will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or lost profits, revenue, data, goodwill, business interruption, failed agent actions, or third-party decisions.",
        "Our total liability for any claim relating to the Service is limited to the amount you paid us for the Service in the 12 months before the event giving rise to the claim. These limits do not apply where prohibited by law.",
      ],
    },
    {
      title: "Indemnification",
      body: [
        "You agree to defend, indemnify, and hold harmless MaitoAI, LLC and our affiliates, officers, employees, contractors, and agents from claims, damages, losses, liabilities, costs, and expenses arising from your content, connected accounts, use of the Service, or violation of these Terms, third-party policies, law, or third-party rights, to the extent permitted by law.",
      ],
    },
    {
      title: "Changes to these Terms",
      body: [
        "We may update these Terms from time to time. If we make material changes, we may notify you through the Service, by email, or by updating the date on this page. Your continued use after updated Terms take effect means you accept them, subject to applicable law.",
      ],
    },
    {
      title: "Governing law",
      body: [
        "These Terms are governed by Delaware law, without regard to conflict-of-law rules. Disputes will be resolved in the state or federal courts located in Delaware, unless applicable law requires a different forum. Nothing in these Terms limits mandatory consumer rights.",
      ],
    },
    {
      title: "Contact",
      body: [
        `Questions about these Terms can be sent to ${LEGAL_CONTACT_EMAIL} or mailed to ${company}.`,
      ],
    },
  ],
} satisfies LegalDocument;

export const privacyDocument = {
  title: "Privacy Policy",
  description:
    "How Maito handles account information, workspace data, AI provider connections, integrations, analytics, and managed environments.",
  intro: [
    'This Privacy Policy explains how Maito ("we," "us," or "our") handles information when you use our website, web, desktop, and mobile applications, hosted services, and related features.',
    "Maito connects clients to environments where conversations, files, credentials, and agent workflows are managed. How information is processed depends on whether you run your own environment, use a managed environment, and which providers and tools you connect.",
  ],
  sections: [
    {
      title: "About us",
      body: [`Maito is operated by ${company}. Contact us at ${LEGAL_CONTACT_EMAIL}.`],
    },
    {
      title: "Information we collect and process",
      body: [
        "We process information needed to operate Maito, secure accounts, run agent workflows, and support connected services.",
      ],
      items: [
        "Account information, such as your name, email address, authentication identifiers, organization and workspace membership, invitations, and login activity.",
        "Workspace and agent data, such as prompts, conversations, outputs, code, files, repository data, documents, knowledge, memory, skills, task history, schedules, checkpoints, and attachments you provide or authorize an agent to access.",
        "Connection information, such as provider and integration identifiers, permission scopes, repository or channel metadata, authorization credentials, API keys, and secrets needed to act on your instructions.",
        "Billing information, such as subscriptions, invoices, credit purchases, usage totals, payment-related identifiers, and tax information. Payment card details are handled by our payment provider and are not stored by Maito.",
        "Technical and security information, such as IP addresses, device and browser details, connection metadata, logs, error traces, cookies, timestamps, and abuse-detection signals.",
        "Product analytics, where enabled, including feature usage, model choices, workflow outcomes, and pseudonymous identifiers.",
        "Support communications and feedback you send to us.",
      ],
    },
    {
      title: "Self-hosted and managed environments",
      body: [
        "In a self-hosted environment, workspace data and credentials are primarily stored on the infrastructure you or your organization control. Running a local environment does not automatically upload all workspace content to us. Account services, remote connection services, optional analytics, and connected providers may still process information needed for those features.",
        "In a managed environment, workspace data and credentials are processed on infrastructure operated for the Service by us and our hosting providers. Your organization's administrators control membership and access. Other authorized members may be able to access shared conversations, files, and workflow history according to their permissions.",
        "Remote and relay connections transmit data between your client and environment. Information available to a connected client depends on its permissions and the environment's configuration.",
      ],
    },
    {
      title: "How we use information",
      body: ["We use information to provide, maintain, secure, and improve the Service."],
      items: [
        "Create and authenticate accounts, manage workspaces, and administer access.",
        "Connect clients to environments and operate managed hosting and remote access.",
        "Run agent tasks, process conversations and files, maintain workspace knowledge and history, and execute workflows at your instruction.",
        "Connect AI providers, repositories, Slack, MCP servers, and other authorized tools.",
        "Process billing, measure usage, enforce plan limits, and provide support.",
        "Monitor reliability, investigate suspicious activity, prevent abuse, and debug errors.",
        "Understand feature usage and improve clients, integrations, documentation, and workflows.",
      ],
    },
    {
      title: "AI providers and connected tools",
      body: [
        "When you use an AI provider or connected tool, relevant prompts, context, code, files, outputs, account identifiers, and instructions may be sent to that service to complete your request. Agents may retrieve data from or send data to connected services using the permissions you grant.",
        "Those services have their own terms, privacy policies, retention rules, and AI training practices. Your provider account settings and agreements may affect how they process information. Review those policies before sending sensitive information or enabling an integration.",
        "Disconnecting an integration stops future authorized access through that connection, but does not automatically delete information already sent to a third party. Requests to delete that information may need to be made directly to the provider.",
      ],
    },
    {
      title: "Product analytics",
      body: [
        "Where enabled, Maito sends product analytics such as feature counts, model choices, task outcomes, and connection or secret counts. Product analytics exclude prompts, messages, code, repository URLs, file paths, skill and tool names, and credential values. Analytics identifiers are pseudonymous; signed-in accounts may be recognized across their environments.",
        "Environment administrators can disable product analytics by setting MAITO_TELEMETRY_ENABLED=false. Development environments do not send product analytics by default. Disabling product analytics does not disable information needed for account services, billing, security, or service operation.",
        "Our hosted website and web app use self-hosted Umami for visit and page-view statistics, referring sites, browser and device types, language, and approximate location. This tracking does not use tracking cookies or identify signed-in accounts. We remove query strings, URL fragments, private task identifiers, page titles, and referrer paths before collection. Local installs, desktop, and native mobile clients do not include this visit tracker. Visit tracking is separate from environment product analytics.",
      ],
    },
    {
      title: "Service providers and other disclosures",
      body: [
        "We share information with vendors that help provide authentication, payments, hosting, storage, networking, monitoring, analytics, security, email, and support. They may process information as needed to provide their services or as otherwise permitted by law.",
        "We may also disclose information when required by law, to protect rights and safety, to investigate abuse, to enforce agreements, or as part of a merger, financing, acquisition, reorganization, or sale of assets.",
      ],
    },
    {
      title: "No sale of personal information",
      body: [
        "We do not sell personal information. We do not use customer prompts, conversations, code, files, or credentials to train our own AI models. Connected AI providers process information under their own policies and your agreements with them.",
      ],
    },
    {
      title: "Data storage and retention",
      body: [
        "Self-hosted data is stored wherever you operate your environment. Managed services and our vendors may process information in the United States and other countries where they operate. Privacy protections may differ from those in your country.",
        "We retain information as needed to provide the Service, comply with legal obligations, resolve disputes, maintain backups, prevent abuse, and support security and billing records. Deleting a managed environment or ending paid access may permanently remove its files and backups; export information you need beforehand.",
        "You may delete certain information through the Service or request deletion by contacting support. Backups, legal obligations, security needs, and billing records may require some information to be retained. We cannot delete data stored solely on infrastructure you control or information independently retained by connected providers.",
      ],
    },
    {
      title: "Security",
      body: [
        "We use reasonable technical and organizational safeguards, including access controls, credential protection, and encryption in transit for hosted services. No internet service is perfectly secure. You are responsible for protecting your devices, credentials, self-hosted environments, and connected accounts, and for configuring access and permissions appropriately.",
      ],
    },
    {
      title: "Cookies and local storage",
      body: [
        "We use cookies, local storage, and similar technologies for authentication, security, preferences, saved connections, and essential functionality. Analytics and operational tools may also process usage information. Browser controls can limit these technologies, but blocking essential storage may prevent parts of Maito from working.",
      ],
    },
    {
      title: "Your choices and rights",
      body: [
        `Depending on where you live, you may have rights to access, correct, delete, restrict, object to, or export personal information, withdraw consent where processing relies on consent, or complain to a relevant supervisory authority. Send requests to ${LEGAL_CONTACT_EMAIL}. We may need to verify your identity and may limit a request where permitted by law.`,
        "For data controlled by your organization or a self-hosted environment administrator, contact that organization or administrator. You can manage connected services and permissions through Maito and the relevant provider's settings.",
      ],
    },
    {
      title: "Children",
      body: [
        "Maito is not intended for children under 13, and we do not knowingly collect personal information from children under 13.",
      ],
    },
    {
      title: "Do Not Track",
      body: [
        "Hosted Umami visit tracking respects browser Do Not Track and Global Privacy Control signals. These signals do not disable essential account services, billing, security, or environment product analytics, which has a separate administrator control.",
      ],
    },
    {
      title: "Changes",
      body: [
        "We may update this Privacy Policy from time to time. If we make material changes, we may notify you through the Service, by email, or by updating the date on this page. The revised policy applies from its effective date, subject to applicable law.",
      ],
    },
    {
      title: "Contact",
      body: [
        `Questions or privacy requests can be sent to ${LEGAL_CONTACT_EMAIL} or mailed to ${company}.`,
      ],
    },
  ],
} satisfies LegalDocument;
